import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { apiService } from '@/services/api'
import { useAuthStore } from './auth'

export const useSubscriptionStore = defineStore('subscription', () => {
  // State
  const subscriptionStatus = ref('active') // 'active', 'expiring_soon', 'expired', 'deactivated', 'suspended'
  const subscriptionEnd = ref(null)
  const daysRemaining = ref(null)
  const isCheckingSubscription = ref(false)
  const lastChecked = ref(null)
  const suspensionData = ref(null) // Store suspension details
  
  // Computed
  const isSubscriptionExpired = computed(() => subscriptionStatus.value === 'expired')
  const isSubscriptionExpiringSoon = computed(() => subscriptionStatus.value === 'expiring_soon')
  const isSubscriptionSuspended = computed(() => subscriptionStatus.value === 'suspended')
  const isAppBlocked = computed(() => 
    subscriptionStatus.value === 'expired' || 
    subscriptionStatus.value === 'deactivated' || 
    subscriptionStatus.value === 'suspended'
  )
  
  // Actions
  const checkSubscription = async () => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated || authStore.userRole === 'superadmin') {
      return { success: true }
    }
    
    // Ensure the API service has the auth token
    if (authStore.token) {
      apiService.setAuthToken(authStore.token)
    } else {
      console.warn('No auth token available for subscription check')
      return { success: false, expired: false }
    }
    
    isCheckingSubscription.value = true
    
    try {
      // Try to make a simple authenticated request to check subscription
      const response = await apiService.get('/dashboard/stats')
      
      // If successful, subscription is active
      subscriptionStatus.value = 'active'
      lastChecked.value = new Date()
      
      // Check for subscription warning headers
      if (response.subscription_warning) {
        subscriptionStatus.value = 'expiring_soon'
        daysRemaining.value = response.days_remaining
      }
      
      return { success: true }
    } catch (error) {
      if ((error.response?.status === 402 || error.response?.status === 403) && error.response?.data?.subscription_status) {
        const data = error.response.data
        subscriptionStatus.value = data.subscription_status
        subscriptionEnd.value = data.subscription_end
        daysRemaining.value = data.days_remaining
        lastChecked.value = new Date()
        
        console.log('Subscription check failed - expired:', data)
        
        return { 
          success: false, 
          expired: true,
          message: data.message 
        }
      }
      
      // Other errors don't affect subscription status
      console.log('Subscription check - other error:', error.message)
      return { success: false, expired: false }
    } finally {
      isCheckingSubscription.value = false
    }
  }
  
  const handleSubscriptionExpired = (eventDetail) => {
    subscriptionStatus.value = eventDetail.subscription_status || 'expired'
    subscriptionEnd.value = eventDetail.subscription_end
    daysRemaining.value = eventDetail.days_remaining
    lastChecked.value = new Date()
    console.log('Subscription expired event handled:', eventDetail)
  }
  
  const resetSubscription = () => {
    subscriptionStatus.value = 'active'
    subscriptionEnd.value = null
    daysRemaining.value = null
    lastChecked.value = null
  }

  const updateSubscriptionStatus = (businessData) => {
    if (!businessData) return
    
    const now = new Date()
    const endDate = new Date(businessData.subscription_end)
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
    
    if (!businessData.is_active) {
      subscriptionStatus.value = 'deactivated'
    } else if (daysUntilExpiry < 0) {
      subscriptionStatus.value = 'expired'
    } else if (daysUntilExpiry <= 7) {
      subscriptionStatus.value = 'expiring_soon'
    } else {
      subscriptionStatus.value = 'active'
    }
    
    subscriptionEnd.value = businessData.subscription_end
    daysRemaining.value = daysUntilExpiry
    lastChecked.value = new Date()
    
    console.log('Subscription status updated:', subscriptionStatus.value)
  }
  
  // Handle subscription suspension
  const handleSubscriptionSuspended = (detail) => {
    console.log('Subscription store - handling suspension event:', detail)
    subscriptionStatus.value = 'suspended'
    suspensionData.value = detail
    lastChecked.value = new Date()
    
    console.log('Subscription suspended - store updated:', {
      status: subscriptionStatus.value,
      isSubscriptionSuspended: isSubscriptionSuspended.value,
      isAppBlocked: isAppBlocked.value
    })
  }

  // Handle subscription reactivation
  const handleSubscriptionReactivated = () => {
    console.log('Subscription store - handling reactivation event')
    // Reset suspension status and data
    if (subscriptionStatus.value === 'suspended') {
      subscriptionStatus.value = 'active'
      suspensionData.value = null
      lastChecked.value = new Date()
      
      console.log('Subscription reactivated - cleared suspension data:', {
        status: subscriptionStatus.value,
        isSubscriptionSuspended: isSubscriptionSuspended.value,
        isAppBlocked: isAppBlocked.value
      })
    } else {
      console.log('Subscription reactivation - was not suspended, current status:', subscriptionStatus.value)
    }
  }

  // Initialize subscription event listeners
  const initializeSubscriptionListener = () => {
    window.addEventListener('subscription-expired', (event) => {
      handleSubscriptionExpired(event.detail)
    })
    
    window.addEventListener('subscription-suspended', (event) => {
      handleSubscriptionSuspended(event.detail)
    })
    
    window.addEventListener('subscription-reactivated', () => {
      handleSubscriptionReactivated()
    })
  }
  
  return {
    // State
    subscriptionStatus: readonly(subscriptionStatus),
    subscriptionEnd: readonly(subscriptionEnd),
    daysRemaining: readonly(daysRemaining),
    isCheckingSubscription: readonly(isCheckingSubscription),
    lastChecked: readonly(lastChecked),
    suspensionData: readonly(suspensionData),
    
    // Computed
    isSubscriptionExpired,
    isSubscriptionExpiringSoon,
    isSubscriptionSuspended,
    isAppBlocked,
    
    // Actions
    checkSubscription,
    handleSubscriptionExpired,
    handleSubscriptionSuspended,
    resetSubscription,
    updateSubscriptionStatus,
    initializeSubscriptionListener
  }
})